import { Link } from 'react-router'
import { Trophy } from 'lucide-react'
import { MatchupArt } from '../brand/MatchupArt.jsx'
import { TeamMark } from '../brand/TeamMark.jsx'
import { accentStyle, cn, roundLabel } from '../../lib/format.js'
import { formatDay, formatFull, formatTime, relativeTime } from '../../lib/time.js'

export const matchHref = (match) => `/e/${match.event.slug}/matches/${match.id}`

function ScoreRow({ team }) {
  const won = Boolean(team?.won)
  return (
    <div className="flex items-center gap-2.5">
      <TeamMark team={team} size="xs" />
      {team ? (
        <Link
          to={`/t/${team.slug}`}
          className={cn('min-w-0 flex-1 truncate rounded text-sm hover:underline', won ? 'font-semibold text-ink' : 'text-ink-muted')}
        >
          {team.name}
          {won && <span className="sr-only"> (winner)</span>}
        </Link>
      ) : (
        <span className="flex-1 text-sm text-ink-muted italic">TBD</span>
      )}
      {won && <Trophy aria-hidden="true" className="size-3.5 text-primary-400" />}
      <span className={cn('w-5 text-right font-display text-lg tabular-nums', won ? 'text-primary-400' : 'text-ink-muted')}>
        {team?.score ?? '–'}
      </span>
    </div>
  )
}

// Scoreboard cards: two rows, winner highlighted, readable at any width.
export function ResultsList({ matches, now }) {
  return (
    <ul className="grid gap-3 sm:grid-cols-2">
      {matches.map((m) => (
        <li
          key={m.id}
          style={accentStyle(m.event.accent)}
          className="space-y-2 rounded-xl bg-surface-2/50 p-3.5 ring-1 ring-white/[0.07] ring-inset"
        >
          <p className="flex items-center gap-1.5 text-xs text-ink-muted">
            <Link to={`/e/${m.event.slug}`} className="tint-text min-w-0 truncate rounded font-medium hover:underline">
              {m.event.name}
            </Link>
            <span aria-hidden="true">·</span>
            <Link to={matchHref(m)} className="shrink-0 rounded hover:text-ink hover:underline">
              {roundLabel(m)}
            </Link>
            <time dateTime={m.completedAt} title={formatFull(m.completedAt)} className="ml-auto shrink-0">
              {relativeTime(m.completedAt, now)}
            </time>
          </p>
          <ScoreRow team={m.team1} />
          <ScoreRow team={m.team2} />
        </li>
      ))}
    </ul>
  )
}

// A compact head-to-head card for team pages and schedules.
export function MatchupCard({ match, now }) {
  return (
    <article className="overflow-hidden rounded-xl bg-surface-2/50 ring-1 ring-white/10 ring-inset" style={accentStyle(match.event.accent)}>
      <MatchupArt team1={match.team1} team2={match.team2} seed={`match-${match.id}`} className="h-24" />
      <div className="space-y-1.5 p-3.5">
        <p className="flex items-center justify-between gap-2 text-sm font-medium">
          <span className="truncate">{match.team1?.name ?? 'TBD'}</span>
          <span className="shrink-0 text-[11px] text-ink-muted uppercase">vs</span>
          <span className="truncate text-right">{match.team2?.name ?? 'TBD'}</span>
        </p>
        <div className="flex items-center justify-between gap-2 text-xs text-ink-muted">
          <Link to={`/e/${match.event.slug}`} className="tint-text truncate rounded font-medium hover:underline">
            {match.event.name}
          </Link>
          <Link to={matchHref(match)} className="shrink-0 rounded hover:text-ink hover:underline">
            {match.status === 'live' ? (
              <span className="font-semibold text-red-300">Live now</span>
            ) : match.scheduledAt ? (
              <time dateTime={match.scheduledAt} title={formatFull(match.scheduledAt)}>
                {formatDay(match.scheduledAt, now)} · {formatTime(match.scheduledAt)}
              </time>
            ) : (
              'Time TBA'
            )}
          </Link>
        </div>
      </div>
    </article>
  )
}
